import { create } from 'zustand';
// NOTE: these come from the raw SDK, so the VITE_E2E alias on
// '@/lib/firebase/firestore' does NOT cover this store — under E2E it still
// talks to real Firestore and fetchFieldMappings() fails with "Missing or
// insufficient permissions". __flowBuilderStore (below) lets a capture spec
// seed the empty-state flow; switching to the wrapper is the real fix but
// changes production read/write behaviour, so it is left to a deliberate pass.
import { collection, doc, addDoc, setDoc, deleteDoc, getDocs, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { useAuthStore, getEffectiveUid } from './auth-store';
import { useAppStore } from './app-store';
import { useModuleStore } from './module-store';
import { EVENT_APPS } from '@/config/constants';
import {
  MMM_ALLOWING_SAAS,
  type FieldMappingDocument,
  type MMMCard,
  type CustomVariable,
  type RecentEvent,
  type SidebarSection,
} from '@/types/field-mapping-flow';
import type { IEventTPAModule, EventType } from '@/types/events';

// ── Helpers ──

/** Get the Firestore collection ref for field mappings */
function getFieldMappingsCollection() {
  const uid = getEffectiveUid();
  const extensionName = useAppStore.getState().extensionName;

  if (!uid || !extensionName) return null;

  return collection(db, 'ulgebraUsers', uid, 'extensions', extensionName, 'fieldMappings');
}

/** Get the Firestore collection ref for recent TPA events */
function getRecentEventsCollection() {
  const uid = getEffectiveUid();
  const extensionName = useAppStore.getState().extensionName;

  if (!uid || !extensionName) return null;

  return collection(
    db,
    'ulgebraUsers',
    uid,
    'extensions',
    extensionName,
    'activityLog',
    'incomingWebhooksEvents',
    'tpa'
  );
}

/** Recursively strip undefined values from an object (Firestore rejects them) */
function stripUndefined(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined) continue;
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      result[key] = stripUndefined(value as Record<string, unknown>);
    } else {
      result[key] = value;
    }
  }
  return result;
}

/** Drop field entries whose mapped value is empty/whitespace, keeping fields_type in sync.
 *  The original app never persists blank field mappings. */
function stripEmptyFields(
  fields: Record<string, string> | undefined,
  fieldsType: Record<string, string> | undefined
): { fields: Record<string, string>; fields_type: Record<string, string> } {
  const cleanedFields: Record<string, string> = {};
  const cleanedTypes: Record<string, string> = {};
  for (const [key, value] of Object.entries(fields ?? {})) {
    if (value == null || (typeof value === 'string' && value.trim().length === 0)) continue;
    cleanedFields[key] = value;
    if (fieldsType && key in fieldsType) cleanedTypes[key] = fieldsType[key];
  }
  return { fields: cleanedFields, fields_type: cleanedTypes };
}

/** Holds the Firestore unsubscribe function for the real-time events listener */
let _recentEventsUnsub: (() => void) | null = null;

/** Extract event type value/name from an event source object */
function parseEventTypeFromSource(
  src: Record<string, unknown>,
  extensionName: string
): { eventTypeValue: string; eventTypeName: string } {
  let eventTypeValue = '';
  let eventTypeName = '';

  try {
    // Acuity Scheduling
    if (extensionName.includes('acuityscheduling') && src.appointmentTypeID) {
      eventTypeValue = String(src.appointmentTypeID);
      if (src.type) eventTypeName = String(src.type);
    }

    // Calendly — try multiple payload structures
    if (extensionName.includes('calendly')) {
      // V1 API
      const eventObj = src.event as Record<string, unknown> | undefined;
      if (eventObj?.event_type) {
        const parts = String(eventObj.event_type).split('/');
        eventTypeValue = parts[parts.length - 1] || '';
      }
      if (eventObj?.name) eventTypeName = String(eventObj.name);

      // V2 API
      const resource = src.resource as Record<string, unknown> | undefined;
      if (!eventTypeValue && resource?.event_type) {
        const parts = String(resource.event_type).split('/');
        eventTypeValue = parts[parts.length - 1] || '';
      }
      if (!eventTypeName && resource?.name) eventTypeName = String(resource.name);

      // Scheduled event nested
      const scheduledEvent = (resource?.scheduled_event || src.scheduled_event) as Record<string, unknown> | undefined;
      if (!eventTypeValue && scheduledEvent?.event_type) {
        const parts = String(scheduledEvent.event_type).split('/');
        eventTypeValue = parts[parts.length - 1] || '';
      }
      if (!eventTypeName && scheduledEvent?.name) eventTypeName = String(scheduledEvent.name);
    }
  } catch {
    // Ignore parse errors
  }

  return { eventTypeValue, eventTypeName };
}

/** Generate a random ID (matches original app's makerandomId) */
function makeRandomId(length = 7): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

// ── Store Interface ──

interface FlowBuilderStore {
  // Data
  fieldMappings: Record<string, FieldMappingDocument>;
  recentEvents: RecentEvent[];
  eventTypes: EventType[];

  // UI state
  selectedBranchId: string | null;
  selectedCardId: string | null;
  editingConditionId: string | null;
  activeSidebarSection: SidebarSection;
  fieldMappingPanelOpen: boolean;

  // Variable form panel state
  variableFormOpen: boolean;
  editingVariableId: string | null;
  isNewVariable: boolean;

  // Dirty tracking (per branch docId)
  dirtyBranches: Set<string>;
  /** Snapshot of field mappings as last saved/loaded — used for discard */
  savedFieldMappings: Record<string, FieldMappingDocument>;

  // Loading
  loading: boolean;
  saving: Record<string, boolean>;
  error: string | null;

  // ── Data Actions ──
  fetchFieldMappings(): Promise<void>;
  fetchRecentEvents(): Promise<void>;
  fetchEventTypeBranches(): Promise<void>;

  /** Start a real-time Firestore listener for new incoming events */
  subscribeToRecentEvents(): void;
  /** Stop the real-time listener */
  unsubscribeRecentEvents(): void;

  saveBranch(docId: string): Promise<void>;
  deleteBranch(docId: string): Promise<void>;

  /** Add a new branch. Returns a temporary ID (prefixed with "new_") */
  addBranch(filterValue: string, filterValueLabel: string, name?: string): string;

  updateBranchName(docId: string, name: string): void;
  updateBranchCondition(docId: string, filterValue: string, filterValueLabel: string): void;

  // ── MMM Card Actions ──
  addCard(docId: string, card: MMMCard): void;
  updateCard(docId: string, cardId: string, updates: Partial<MMMCard>): void;
  removeCard(docId: string, cardId: string): void;
  reorderCards(docId: string, fromIndex: number, toIndex: number): void;

  // ── Variable Actions ──
  addVariable(docId: string, variable: CustomVariable): void;
  updateVariable(docId: string, varId: string, updates: Partial<CustomVariable>): void;
  removeVariable(docId: string, varId: string): void;

  // ── Node Position Actions ──
  /** Update cached canvas node positions for a branch (marks dirty) */
  updateNodePositions(docId: string, positions: Record<string, { x: number; y: number }>): void;

  /** Discard all unsaved changes — restore field mappings to last saved state */
  discardChanges(): void;

  // ── UI Actions ──
  markDirty(docId: string): void;
  selectBranch(docId: string | null): void;
  selectCard(cardId: string | null): void;
  setFieldMappingPanelOpen(open: boolean): void;
  setActiveSidebarSection(section: SidebarSection): void;
  setEditingConditionId(docId: string | null): void;
  setLoading(loading: boolean): void;

  // Variable form panel
  openVariableForm(varId: string | null, isNew: boolean): void;
  closeVariableForm(): void;

  // ── Reset ──
  reset(): void;
}

// ── Store ──

export const useFlowBuilderStore = create<FlowBuilderStore>((set, get) => ({
  // Initial state
  fieldMappings: {},
  recentEvents: [],
  eventTypes: [],
  selectedBranchId: null,
  selectedCardId: null,
  editingConditionId: null,
  activeSidebarSection: 'modules',
  fieldMappingPanelOpen: false,
  variableFormOpen: false,
  editingVariableId: null,
  isNewVariable: false,
  dirtyBranches: new Set(),
  savedFieldMappings: {},
  loading: true,
  saving: {},
  error: null,

  // ── Data Actions ──

  fetchFieldMappings: async () => {
    const colRef = getFieldMappingsCollection();
    if (!colRef) {
      console.warn('[FlowBuilder] Cannot fetch: missing user/app context');
      return;
    }

    set({ loading: true, error: null });

    try {
      const q = query(colRef, orderBy('ct', 'desc'), limit(20));
      const snapshot = await getDocs(q);

      const mappings: Record<string, FieldMappingDocument> = {};
      const appCodeSAAS = useAppStore.getState().appCodeSAAS;
      const isMultiModule = appCodeSAAS
        ? (MMM_ALLOWING_SAAS as readonly string[]).includes(appCodeSAAS)
        : false;

      snapshot.forEach((docSnap) => {
        const data = docSnap.data() as FieldMappingDocument;
        // Skip incomplete/unset mappings
        if (data.filterValue === '---unset---') return;
        mappings[docSnap.id] = { ...data, id: docSnap.id };

        // Convert flat non-MMM data from Firestore to internal card format
        // so the UI can work consistently with card-based structure.
        // Flat format: { saasFieldModuleName, fields: {apiName: value}, fields_type: {...} }
        // Card format: { isMMM: true, fields: { cardId: { cardId, saasFieldModuleName, fields, ... } } }
        if (!isMultiModule) {
          const entry = mappings[docSnap.id];
          if (!entry.isMMM && entry.saasFieldModuleName && entry.fields) {
            const flatFields = entry.fields as Record<string, string>;
            const cardId = makeRandomId(7);
            const card: MMMCard = {
              cardId,
              saasFieldModuleName: entry.saasFieldModuleName,
              displayLabel: entry.saasFieldModuleName,
              orderOf: 0,
              fields: flatFields,
              fields_type: entry.fields_type || {},
              ct: entry.ct || Date.now(),
            };
            entry.isMMM = true;
            entry.fields = { [cardId]: card } as unknown as Record<string, MMMCard>;
          }
        }

        const loaded = mappings[docSnap.id];
        if (loaded.isMMM && loaded.fields) {
          for (const card of Object.values(loaded.fields as Record<string, MMMCard>)) {
            if (typeof card !== 'object' || card === null || !('cardId' in card)) continue;
            if (card.alwaysCreateNew === true && card.syncToExisting === undefined) card.syncToExisting = false;
          }
        }
      });

      // Ensure a ---default branch always exists (locally if not in Firestore)
      const hasDefault = Object.values(mappings).some(
        (m) => m.filterValue === '---default'
      );
      if (!hasDefault) {
        const tempId = `new_${makeRandomId(10)}`;
        mappings[tempId] = {
          id: tempId,
          name: 'Default',
          filterValue: '---default',
          filterValueLabel: 'Default for All',
          fieldMappingType: 'TPA',
          ct: Date.now(),
          isMMM: true,
          fields: {},
          variables: {},
        };
      }

      set({ fieldMappings: mappings, savedFieldMappings: JSON.parse(JSON.stringify(mappings)) });
      console.log(`[FlowBuilder] Loaded ${Object.keys(mappings).length} field mappings`);
    } catch (error) {
      console.error('[FlowBuilder] Failed to fetch field mappings:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to load field mappings',
      });
    }
  },

  fetchRecentEvents: async () => {
    const colRef = getRecentEventsCollection();
    if (!colRef) return;

    try {
      const extensionName = useAppStore.getState().extensionName || '';
      const q = query(colRef, orderBy('ct', 'desc'), limit(15));
      const snapshot = await getDocs(q);

      const events: RecentEvent[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const parsed = parseEventTypeFromSource(data.actualSourceObj || {}, extensionName);
        events.push({
          id: docSnap.id,
          title: data.title || 'Untitled Event',
          ct: data.ct || 0,
          actualSourceObj: data.actualSourceObj || {},
          eventTypeValue: parsed.eventTypeValue,
          eventTypeName: parsed.eventTypeName,
        });
      });

      set({ recentEvents: events });
      console.log(`[FlowBuilder] Loaded ${events.length} recent events`);
    } catch (error) {
      console.error('[FlowBuilder] Failed to fetch recent events:', error);
    }
  },

  subscribeToRecentEvents: () => {
    // Clean up any existing listener
    if (_recentEventsUnsub) {
      _recentEventsUnsub();
      _recentEventsUnsub = null;
    }

    const colRef = getRecentEventsCollection();
    if (!colRef) return;

    const extensionName = useAppStore.getState().extensionName || '';
    const q = query(colRef, orderBy('ct', 'desc'), limit(15));

    _recentEventsUnsub = onSnapshot(q, (snapshot) => {
      const events: RecentEvent[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const parsed = parseEventTypeFromSource(data.actualSourceObj || {}, extensionName);
        events.push({
          id: docSnap.id,
          title: data.title || 'Untitled Event',
          ct: data.ct || 0,
          actualSourceObj: data.actualSourceObj || {},
          eventTypeValue: parsed.eventTypeValue,
          eventTypeName: parsed.eventTypeName,
        });
      });
      set({ recentEvents: events });
      console.log(`[FlowBuilder] Real-time: ${events.length} recent events`);
    }, (error) => {
      console.error('[FlowBuilder] Real-time events listener error:', error);
    });
  },

  unsubscribeRecentEvents: () => {
    if (_recentEventsUnsub) {
      _recentEventsUnsub();
      _recentEventsUnsub = null;
      console.log('[FlowBuilder] Unsubscribed from recent events');
    }
  },

  fetchEventTypeBranches: async () => {
    const appCodeTPA = useAppStore.getState().appCodeTPA;
    const isEventApp = appCodeTPA != null && (EVENT_APPS as readonly string[]).includes(appCodeTPA);

    if (!isEventApp) return;

    try {
      // Get the TPA module and cast to IEventTPAModule
      const tpaModule = useModuleStore.getState().tpaModule;
      if (!tpaModule) {
        console.warn('[FlowBuilder] No TPA module loaded, cannot fetch event types');
        return;
      }

      // Check if the module implements getEventTypes (IEventTPAModule)
      const eventModule = tpaModule as unknown as IEventTPAModule;
      if (typeof eventModule.getEventTypes !== 'function') {
        console.warn('[FlowBuilder] TPA module does not support getEventTypes');
        return;
      }

      // Calendly: use ORG-level event types fetched FRESH (uncached) — matches the
      // incoming-sync settings page. Other event apps keep the user-scoped fetch.
      const useOrgFresh =
        appCodeTPA === 'calendly' && typeof eventModule.getOrgEventTypes === 'function';
      const eventTypes = useOrgFresh
        ? await eventModule.getOrgEventTypes(true)
        : await eventModule.getEventTypes();
      console.log(`[FlowBuilder] Fetched ${eventTypes.length} event types from API${useOrgFresh ? ' (org, uncached)' : ''}`);

      // Store event types for booking URLs and branch filtering
      set({ eventTypes });

      if (eventTypes.length === 0) return;

      const currentMappings = { ...get().fieldMappings };
      const existingFilterValues = new Set(
        Object.values(currentMappings).map((m) => m.filterValue)
      );

      let addedCount = 0;
      eventTypes.forEach((et) => {
        // Extract the event type ID from URI (last segment of URL)
        const etId = et.id || (et.uri ? et.uri.split('/').pop() : '') || '';
        if (!etId) return;

        const filterValue = `eventtype=${etId}`;
        if (!existingFilterValues.has(filterValue)) {
          const displayName = et.name || etId;
          const tempId = `new_${makeRandomId(10)}`;
          currentMappings[tempId] = {
            id: tempId,
            name: displayName,
            filterValue,
            filterValueLabel: displayName,
            fieldMappingType: 'TPA',
            ct: Date.now() + addedCount,
            isMMM: true,
            fields: {},
            variables: {},
          };
          addedCount++;
        }
      });

      if (addedCount > 0) {
        set({
          fieldMappings: currentMappings,
          // Update saved snapshot so discard doesn't remove auto-generated event branches
          savedFieldMappings: JSON.parse(JSON.stringify(currentMappings)),
        });
        console.log(`[FlowBuilder] Auto-generated ${addedCount} event type branches from API`);
      }
    } catch (error) {
      console.error('[FlowBuilder] Failed to fetch event type branches:', error);
    }
  },

  saveBranch: async (docId: string) => {
    const colRef = getFieldMappingsCollection();
    if (!colRef) return;

    const mapping = get().fieldMappings[docId];
    if (!mapping) {
      console.warn('[FlowBuilder] No mapping found for docId:', docId);
      return;
    }

    // ── Skip saving branches with no configured fields ──
    // Matches original app behavior: only save if there are actual field mappings.
    // Prevents empty branch documents from being stored in Firestore, which would
    // be treated as active field mapping items by the execution engine.
    const fields = mapping.fields || {};
    const fieldKeys = Object.keys(fields);
    let hasConfiguredFields = false;

    if (fieldKeys.length > 0) {
      if (mapping.isMMM) {
        // MMM mode: check if any MMMCard has non-empty fields (create or update)
        hasConfiguredFields = fieldKeys.some((key) => {
          const card = fields[key];
          if (typeof card === 'object' && card !== null && 'cardId' in card) {
            const mmmCard = card as MMMCard;
            const createFields = Object.keys(mmmCard.fields || {}).length;
            const updateFields = Object.keys(mmmCard.fieldsForExisting?.fields || {}).length;
            return createFields > 0 || updateFields > 0;
          }
          return false;
        });
      } else {
        // Non-MMM (legacy): fields is Record<string, string>, any entry counts
        hasConfiguredFields = true;
      }
    }

    if (!hasConfiguredFields) {
      if (docId.startsWith('new_')) {
        // New branch with no fields — don't create in Firestore, just clear dirty flag
        console.log(`[FlowBuilder] Skipping save for ${docId} — no fields configured`);
        const newDirty = new Set(get().dirtyBranches);
        newDirty.delete(docId);
        set({ dirtyBranches: newDirty });
      } else {
        // Existing branch with all fields/cards removed — delete from Firestore
        // so the execution engine doesn't treat it as an active field mapping.
        console.log(`[FlowBuilder] Deleting ${docId} from Firestore — all fields removed`);
        try {
          const docRef = doc(colRef, docId);
          await deleteDoc(docRef);

          const newDirty = new Set(get().dirtyBranches);
          newDirty.delete(docId);
          const updatedSaved = { ...get().savedFieldMappings };
          delete updatedSaved[docId];
          set({ dirtyBranches: newDirty, savedFieldMappings: updatedSaved });
        } catch (error) {
          console.error('[FlowBuilder] Failed to delete empty branch:', error);
        }
      }
      return;
    }

    set({ saving: { ...get().saving, [docId]: true } });

    try {
      // Build the document without the local `id` field, stripping undefined values
      const { id: _id, ...rawDocData } = mapping;

      // For non-MMM apps, flatten the internal card structure back to root-level fields.
      // Original app/execution engine expects: { saasFieldModuleName, fields: {apiName: value}, fields_type }
      // NOT the card wrapper: { isMMM: true, fields: { cardId: { cardId, fields, ... } } }
      const appCodeSAAS = useAppStore.getState().appCodeSAAS;
      const isMultiModule = appCodeSAAS
        ? (MMM_ALLOWING_SAAS as readonly string[]).includes(appCodeSAAS)
        : false;

      if (!isMultiModule && rawDocData.isMMM) {
        const cardEntries = Object.values(rawDocData.fields || {}).filter(
          (v): v is MMMCard => typeof v === 'object' && v !== null && 'cardId' in v
        );
        if (cardEntries.length > 0) {
          const card = cardEntries[0];
          rawDocData.saasFieldModuleName = card.saasFieldModuleName;
          rawDocData.fields = card.fields;
          rawDocData.fields_type = card.fields_type;
          delete rawDocData.isMMM;
          delete rawDocData.variables;
        }
      }

      // Drop empty field mappings before persisting (mirror original — blank values aren't saved).
      if (rawDocData.isMMM && rawDocData.fields) {
        const cards = rawDocData.fields as Record<string, MMMCard>;
        const cleanedCards: Record<string, MMMCard> = {};
        for (const [cardId, card] of Object.entries(cards)) {
          const { fields, fields_type } = stripEmptyFields(card.fields, card.fields_type);
          // Also clean the update-existing map — the original builds it from the same
          // empty-stripped fieldsMap, so blank values must not persist there either.
          let fieldsForExisting = card.fieldsForExisting;
          if (fieldsForExisting) {
            const cleanedUpdate = stripEmptyFields(fieldsForExisting.fields, fieldsForExisting.fields_type);
            fieldsForExisting = {
              ...fieldsForExisting,
              fields: cleanedUpdate.fields,
              fields_type: cleanedUpdate.fields_type,
            };
          }
          cleanedCards[cardId] = {
            ...card,
            fields,
            fields_type,
            ...(fieldsForExisting && { fieldsForExisting }),
          };
        }
        rawDocData.fields = cleanedCards;
      } else {
        const cleaned = stripEmptyFields(
          rawDocData.fields as Record<string, string> | undefined,
          rawDocData.fields_type
        );
        rawDocData.fields = cleaned.fields;
        rawDocData.fields_type = cleaned.fields_type;
      }

      const docData = stripUndefined(rawDocData as Record<string, unknown>);

      if (docId.startsWith('new_')) {
        // New document — use add()
        const docRef = await addDoc(colRef, docData);
        const newId = docRef.id;

        // Replace temp ID with real Firestore ID in an atomic read-then-set
        const {
          fieldMappings,
          dirtyBranches,
          selectedBranchId,
          saving: currentSaving,
        } = get();
        const updated = { ...fieldMappings };
        delete updated[docId];
        // Use original mapping (card format) for internal state, NOT flattened docData
        updated[newId] = { ...mapping, id: newId };

        const newDirty = new Set(dirtyBranches);
        newDirty.delete(docId);

        // If this branch was selected, update selectedBranchId to the real ID
        const newSelectedBranchId =
          selectedBranchId === docId ? newId : selectedBranchId;

        // Move saving flag from temp ID to new ID
        const updatedSaving = { ...currentSaving };
        delete updatedSaving[docId];

        set({
          fieldMappings: updated,
          dirtyBranches: newDirty,
          savedFieldMappings: JSON.parse(JSON.stringify(updated)),
          selectedBranchId: newSelectedBranchId,
          saving: updatedSaving,
        });
        console.log(`[FlowBuilder] Created new mapping: ${newId} (was ${docId})`);
      } else {
        // Existing document — use set()
        const docRef = doc(colRef, docId);
        await setDoc(docRef, docData);

        // Clear dirty flag and update saved snapshot
        const newDirty = new Set(get().dirtyBranches);
        newDirty.delete(docId);
        const currentMappings = get().fieldMappings;
        set({ dirtyBranches: newDirty, savedFieldMappings: JSON.parse(JSON.stringify(currentMappings)) });
        console.log(`[FlowBuilder] Updated mapping: ${docId}`);
      }
    } catch (error) {
      console.error('[FlowBuilder] Failed to save branch:', error);
    } finally {
      set({ saving: { ...get().saving, [docId]: false } });
    }
  },

  deleteBranch: async (docId: string) => {
    const colRef = getFieldMappingsCollection();
    if (!colRef) return;

    try {
      if (docId.startsWith('new_')) {
        // Not yet saved — just remove from local state (and from saved snapshot if it was auto-generated)
        const updated = { ...get().fieldMappings };
        delete updated[docId];
        const updatedSaved = { ...get().savedFieldMappings };
        delete updatedSaved[docId];
        set({ fieldMappings: updated, savedFieldMappings: updatedSaved });
      } else {
        const docRef = doc(colRef, docId);
        await deleteDoc(docRef);

        const updated = { ...get().fieldMappings };
        delete updated[docId];
        const updatedSaved = { ...get().savedFieldMappings };
        delete updatedSaved[docId];

        const newDirty = new Set(get().dirtyBranches);
        newDirty.delete(docId);

        set({ fieldMappings: updated, savedFieldMappings: updatedSaved, dirtyBranches: newDirty });
        console.log(`[FlowBuilder] Deleted mapping: ${docId}`);
      }
    } catch (error) {
      console.error('[FlowBuilder] Failed to delete branch:', error);
    }
  },

  addBranch: (filterValue, filterValueLabel, name) => {
    const tempId = `new_${makeRandomId(10)}`;
    const newMapping: FieldMappingDocument = {
      id: tempId,
      name: name || filterValueLabel || 'New Mapping',
      filterValue,
      filterValueLabel,
      fieldMappingType: 'TPA',
      ct: Date.now(),
      isMMM: true,
      fields: {},
      variables: {},
    };

    set({
      fieldMappings: { ...get().fieldMappings, [tempId]: newMapping },
      dirtyBranches: new Set([...get().dirtyBranches, tempId]),
    });

    return tempId;
  },

  updateBranchName: (docId, name) => {
    const mapping = get().fieldMappings[docId];
    if (!mapping) return;

    set({
      fieldMappings: {
        ...get().fieldMappings,
        [docId]: { ...mapping, name },
      },
    });
    get().markDirty(docId);
  },

  updateBranchCondition: (docId, filterValue, filterValueLabel) => {
    const mapping = get().fieldMappings[docId];
    if (!mapping) return;

    set({
      fieldMappings: {
        ...get().fieldMappings,
        [docId]: { ...mapping, filterValue, filterValueLabel },
      },
    });
    get().markDirty(docId);
  },

  // ── MMM Card Actions ──

  addCard: (docId, card) => {
    const mapping = get().fieldMappings[docId];
    if (!mapping) return;

    const fields = (mapping.fields || {}) as Record<string, MMMCard>;
    // Compute next orderOf from existing cards to maintain insertion order
    const maxOrder = Object.values(fields)
      .filter((v): v is MMMCard => typeof v === 'object' && v !== null && 'orderOf' in v)
      .reduce((max, c) => Math.max(max, c.orderOf ?? 0), -1);
    const cardWithOrder = { ...card, orderOf: maxOrder + 1 };

    set({
      fieldMappings: {
        ...get().fieldMappings,
        [docId]: {
          ...mapping,
          isMMM: true,
          fields: { ...fields, [card.cardId]: cardWithOrder },
        },
      },
    });
    get().markDirty(docId);
  },

  updateCard: (docId, cardId, updates) => {
    const mapping = get().fieldMappings[docId];
    if (!mapping || !mapping.fields) return;

    const fields = mapping.fields as Record<string, MMMCard>;
    const existingCard = fields[cardId];
    if (!existingCard) return;

    set({
      fieldMappings: {
        ...get().fieldMappings,
        [docId]: {
          ...mapping,
          fields: {
            ...fields,
            [cardId]: { ...existingCard, ...updates },
          },
        },
      },
    });
    get().markDirty(docId);
  },

  removeCard: (docId, cardId) => {
    const mapping = get().fieldMappings[docId];
    if (!mapping || !mapping.fields) return;

    const fields = { ...(mapping.fields as Record<string, MMMCard>) };
    delete fields[cardId];

    set({
      fieldMappings: {
        ...get().fieldMappings,
        [docId]: { ...mapping, fields },
      },
    });
    get().markDirty(docId);
  },

  reorderCards: (docId, fromIndex, toIndex) => {
    const mapping = get().fieldMappings[docId];
    if (!mapping?.fields || !mapping.isMMM) return;

    // Get sorted cards array
    const cards = Object.values(mapping.fields as Record<string, MMMCard>)
      .filter((val): val is MMMCard =>
        typeof val === 'object' && val !== null && 'cardId' in val
      )
      .sort((a, b) => (a.orderOf ?? 0) - (b.orderOf ?? 0));

    if (fromIndex < 0 || fromIndex >= cards.length || toIndex < 0 || toIndex >= cards.length) return;

    // Move card from fromIndex to toIndex
    const reordered = [...cards];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);

    // Rebuild fields with updated orderOf
    const updatedFields: Record<string, MMMCard> = {};
    reordered.forEach((card, idx) => {
      updatedFields[card.cardId] = { ...card, orderOf: idx };
    });

    set({
      fieldMappings: {
        ...get().fieldMappings,
        [docId]: { ...mapping, fields: updatedFields },
      },
    });
    get().markDirty(docId);
  },

  // ── Variable Actions ──

  addVariable: (docId, variable) => {
    const mapping = get().fieldMappings[docId];
    if (!mapping) return;

    const variables = mapping.variables || {};
    set({
      fieldMappings: {
        ...get().fieldMappings,
        [docId]: {
          ...mapping,
          variables: { ...variables, [variable.id]: variable },
        },
      },
    });
    get().markDirty(docId);
  },

  updateVariable: (docId, varId, updates) => {
    const mapping = get().fieldMappings[docId];
    if (!mapping || !mapping.variables) return;

    const existing = mapping.variables[varId];
    if (!existing) return;

    set({
      fieldMappings: {
        ...get().fieldMappings,
        [docId]: {
          ...mapping,
          variables: {
            ...mapping.variables,
            [varId]: { ...existing, ...updates },
          },
        },
      },
    });
    get().markDirty(docId);
  },

  removeVariable: (docId, varId) => {
    const mapping = get().fieldMappings[docId];
    if (!mapping || !mapping.variables) return;

    const variables = { ...mapping.variables };
    delete variables[varId];

    set({
      fieldMappings: {
        ...get().fieldMappings,
        [docId]: { ...mapping, variables },
      },
    });
    get().markDirty(docId);
  },

  // ── Node Position Actions ──

  updateNodePositions: (docId, positions) => {
    const mapping = get().fieldMappings[docId];
    if (!mapping) return;

    set({
      fieldMappings: {
        ...get().fieldMappings,
        [docId]: {
          ...mapping,
          nodePositions: { ...mapping.nodePositions, ...positions },
        },
      },
    });
    get().markDirty(docId);
  },

  discardChanges: () => {
    const { savedFieldMappings, selectedBranchId } = get();
    const restored: Record<string, FieldMappingDocument> = JSON.parse(JSON.stringify(savedFieldMappings));

    // If currently selected branch doesn't exist in saved state, deselect it
    const branchStillExists = selectedBranchId && restored[selectedBranchId];

    set({
      fieldMappings: restored,
      dirtyBranches: new Set(),
      selectedBranchId: branchStillExists ? selectedBranchId : null,
      selectedCardId: null,
      editingConditionId: null,
      fieldMappingPanelOpen: false,
      activeSidebarSection: 'modules',
    });
  },

  // ── UI Actions ──

  markDirty: (docId) => {
    set({ dirtyBranches: new Set([...get().dirtyBranches, docId]) });
  },

  selectBranch: (docId) => {
    set({ selectedBranchId: docId, selectedCardId: null, fieldMappingPanelOpen: false, variableFormOpen: false, editingVariableId: null, isNewVariable: false });
  },

  selectCard: (cardId) => {
    set({
      selectedCardId: cardId,
      fieldMappingPanelOpen: !!cardId,
      activeSidebarSection: cardId ? 'fields' : get().activeSidebarSection,
    });
  },

  setFieldMappingPanelOpen: (open) => {
    set({
      fieldMappingPanelOpen: open,
      ...(!open ? { selectedCardId: null, activeSidebarSection: 'modules' } : {}),
    });
  },

  setActiveSidebarSection: (section) => {
    set({ activeSidebarSection: section });
  },

  setEditingConditionId: (docId) => {
    set({ editingConditionId: docId });
  },

  setLoading: (loading) => {
    set({ loading });
  },

  // ── Variable Form Panel ──

  openVariableForm: (varId, isNew) => {
    set({
      variableFormOpen: true,
      editingVariableId: varId,
      isNewVariable: isNew,
    });
  },

  closeVariableForm: () => {
    set({
      variableFormOpen: false,
      editingVariableId: null,
      isNewVariable: false,
    });
  },

  // ── Reset ──
  reset: () => {
    // Clean up real-time listener
    get().unsubscribeRecentEvents();
    set({
      fieldMappings: {},
      savedFieldMappings: {},
      recentEvents: [],
      eventTypes: [],
      selectedBranchId: null,
      selectedCardId: null,
      editingConditionId: null,
      activeSidebarSection: 'modules',
      fieldMappingPanelOpen: false,
      variableFormOpen: false,
      editingVariableId: null,
      isNewVariable: false,
      dirtyBranches: new Set(),
      loading: false,
      saving: {},
      error: null,
    });
  },
}));

// Expose store for E2E testing — the docgen capture specs seed the empty-state
// flow here because fetchFieldMappings() cannot reach Firestore under VITE_E2E
// (see the firestore import note at the top). Mirrors the same DEV-only
// exposure as __moduleStore and __uiStore.
if (import.meta.env.DEV) {
  (window as unknown as Record<string, unknown>).__flowBuilderStore = useFlowBuilderStore;
}
